
import { Component, Watch, Prop, Vue } from 'vue-property-decorator';
import HeaderTitle from '../components/HeaderTitle.vue';
import ChatWithUs from '../components/ChatWithUs.vue';
import { FileDownloadList } from '@/collections/file_downloads';
import { FileDownloadModel } from '@/models/file_download';
import { StoreModel } from '@/models/store';
import { MeasureList } from '@/collections/measures';
import {get} from "@/services/http";
import {displayAlert} from "@/services/alert";

@Component({
  components: {
    HeaderTitle,
    ChatWithUs,
  },
})
export default class DownloadManager extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  public success: boolean = false;
  public error: boolean = false;
  public isLoading: boolean = true;
  public files: FileDownloadList = new FileDownloadList();
  public fileHeaders: any = [];
  public regeneratingFiles: Set<number> = new Set();
  public async created() {

    this.fileHeaders = [
      {
        text: 'ID',
        value: 'id',
        width: '',
      },
      {
        text: 'Name',
        value: 'downloadName',
        width: '',
      },
      {
        text: 'Created',
        value: 'createdAt',
        width: '',
      },
      {
        text: 'Recipient Email',
        value: 'emails',
        width: '',
      },
      {
        text: 'Params',
        value: 'params',
        width: '',
      },
      {
        text: 'Status',
        value: 'status',
        align: '',
      },
      {
        text: 'Action',
        value: 'link',
        align: '',
      },
    ];
  }

  public async fullLoadData() {
    this.isLoading = true;
    this.files = new FileDownloadList();
    await this.files.fetch();

    for (const file of this.files.items) {
      for (const measure of this.measures.items) {
        if (file.measure.code === measure.code && measure.code) {
          file.measure = measure;
          break;
        }
      }

      for (const dimension of file.measure.dimensions.items) {
        if (file.dimension.code === dimension.code && dimension.code) {
          file.dimension = dimension;
          break;
        }
      }
    }
    this.isLoading = false;
  }

  public async regenerate(fileItem: FileDownloadModel) {
    // Add file to regenerating set to show loading
    this.regeneratingFiles.add(fileItem.id);
    this.$forceUpdate(); // Force Vue to re-render

    try {
      await get('/regenerate', {fileId: fileItem.id});
      fileItem.status = 'EXPORTING';
      displayAlert('The exported file will be sent to emails above, please check your inbox');
    } catch (e) {
      displayAlert('Something went wrong, please try again later')
    } finally {
      // Remove from regenerating set when done
      this.regeneratingFiles.delete(fileItem.id);
      this.$forceUpdate(); // Force Vue to re-render
    }
  }

  public isRegenerating(fileId: number): boolean {
    return this.regeneratingFiles.has(fileId);
  }

  public convertDateStr(dateStr: string) {
    if (!dateStr) {
      return '';
    }
    const date = new Date(dateStr);
    const timezoneOffset = 7 * 60;
    const adjustedDate = new Date(date.getTime() + timezoneOffset * 60 * 1000);
    const padZero = (num: number) => (num < 10 ? '0' : '') + num;
    return `${adjustedDate.getUTCFullYear()}-${padZero(adjustedDate.getUTCMonth() + 1)}-${padZero(adjustedDate.getUTCDate())} ${padZero(adjustedDate.getUTCHours())}:${padZero(adjustedDate.getUTCMinutes())}:${padZero(adjustedDate.getUTCSeconds())}`;
  }

  public getEffectiveStatus(file: any): string {
    // Check if export has been running for more than 1 day
    if (file.status === 'EXPORTING' && file.createdAt) {
      const createdDate = new Date(file.createdAt);
      const now = new Date();
      const diffMs = now.getTime() - createdDate.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

      // If more than 4 hours, treat as expired
      if (diffHours >= 4) {
        return 'EXPIRED';
      }
    }
    return file.status || '';
  }

  public getStatusColor(status: string): string {
    switch(status) {
      case 'EXPORTED': return 'success';
      case 'EXPORTING': return 'primary';
      case 'EXPIRED': return 'warning';
      case 'FAILED': return 'error';
      default: return 'grey';
    }
  }

  private formatCodeToReadable(code: string): string {
    if (!code) return '';

    // Replace underscores with spaces
    let formatted = code.replace(/_/g, ' ');

    // Capitalize first letter of each word
    formatted = formatted.replace(/\b\w/g, (char) => char.toUpperCase());

    // Special handling for common prefixes
    formatted = formatted.replace(/^By\s/i, 'by ');
    formatted = formatted.replace(/^Per\s/i, 'per ');
    formatted = formatted.replace(/^With\s/i, 'with ');
    formatted = formatted.replace(/^In\s/i, 'in ');
    formatted = formatted.replace(/^At\s/i, 'at ');
    formatted = formatted.replace(/^On\s/i, 'on ');
    formatted = formatted.replace(/^For\s/i, 'for ');

    // Ensure first letter is always capitalized (except for special prefixes)
    if (!formatted.match(/^(by|per|with|in|at|on|for)\s/)) {
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }

    return formatted;
  }

  public getDisplayName(model: any): string {
    if (model && model.name) {
      return model.name;
    } else if (model && model.code) {
      return this.formatCodeToReadable(model.code);
    }
    return '';
  }

  public getDownloadDisplayName(file: any): string {
    if (file.downloadName) {
      return file.downloadName;
    }

    // If no download name, build a name from measure and dimension
    let displayName = '';
    if (file.measure) {
      displayName = this.getDisplayName(file.measure);
      if (file.dimension) {
        displayName += ` - ${this.getDisplayName(file.dimension)}`;
      }
    }

    return displayName || 'Unnamed Export';
  }


  @Watch('measures', { immediate: true, deep: true })
  private async onMeasuresChanged(newVal: any) {
    if (this.measures.items.length > 0) {
      await this.fullLoadData();
    }
  }
}
